import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('assagart_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Refresh user error:', error);
      return null;
    }
  };

  const login = async (email, password, twoFactorCode = null) => {
    try {
      console.log('AuthContext: Calling login API...');
      const payload = { email, password };
      if (twoFactorCode) {
        payload.two_factor_code = twoFactorCode;
      }
      
      const response = await axios.post(`${API}/auth/login`, payload);
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        console.log('AuthContext: 2FA required');
        return { requires_2fa: true };
      }
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('assagart_token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Update state synchronously
      setToken(access_token);
      setUser(userData);
      
      return userData;
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      throw error;
    }
  };

  const register = async (email, password, first_name, last_name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, first_name, last_name });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('assagart_token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('assagart_token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, refreshUser, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
};
